import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { Check, X } from "lucide-react";
import { PRODUCT_NAME, PRODUCT_NAME_ZH } from "@/lib/publicLinks";
import { openNativeApp } from "@/lib/authEmailLinks";
import { supabase } from "@/lib/supabase";

type Strength = {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  barClass: string;
  hintClass: string;
};

function evaluatePassword(password: string): Strength {
  const checks = {
    length: password.length >= 8,
    mixed: /[a-z]/.test(password) && /[A-Z]/.test(password),
    number: /\d/.test(password),
    symbol: /[^A-Za-z0-9]/.test(password),
  };
  const passed = Object.values(checks).filter(Boolean).length;
  if (!password) {
    return { score: 0, label: "輸入後會顯示強度", barClass: "bg-zinc-200", hintClass: "text-muted-foreground" };
  }
  if (password.length < 6) {
    return {
      score: 1,
      label: "還差一點：至少需要 6 碼才能儲存",
      barClass: "bg-sky-400",
      hintClass: "text-sky-700 dark:text-sky-300",
    };
  }
  if (passed <= 1) {
    return {
      score: 1,
      label: "還可以再加強一點",
      barClass: "bg-amber-300",
      hintClass: "text-amber-800 dark:text-amber-200",
    };
  }
  if (passed === 2) {
    return {
      score: 2,
      label: "普通，可以再加數字或符號",
      barClass: "bg-amber-400",
      hintClass: "text-amber-800 dark:text-amber-200",
    };
  }
  if (passed === 3) {
    return {
      score: 3,
      label: "不錯，已經比較安心",
      barClass: "bg-emerald-400",
      hintClass: "text-emerald-700 dark:text-emerald-300",
    };
  }
  return {
    score: 4,
    label: "很棒，強度足夠",
    barClass: "bg-emerald-600",
    hintClass: "text-emerald-700 dark:text-emerald-300",
  };
}

function passwordChecks(password: string) {
  return [
    { ok: password.length >= 6, text: "至少 6 碼（必填）" },
    { ok: password.length >= 8, text: "8 碼以上（建議）" },
    { ok: /[a-z]/.test(password) && /[A-Z]/.test(password), text: "含大小寫" },
    { ok: /\d/.test(password) || /[^A-Za-z0-9]/.test(password), text: "含數字或符號" },
  ];
}

/** Map Supabase / browser auth errors to calm Chinese copy. */
function friendlyResetError(raw: string): string {
  const msg = String(raw || "");
  const lower = msg.toLowerCase();
  if (
    lower.includes("different from the old") ||
    lower.includes("same as") ||
    lower.includes("should be different") ||
    lower.includes("new password should be different")
  ) {
    return "新密碼不能和舊密碼相同，請換一組再試。";
  }
  if (lower.includes("weak") || (lower.includes("password") && lower.includes("least"))) {
    return "密碼再加強一點後即可儲存（至少 6 碼）。";
  }
  return msg || "更新失敗，請稍後再試";
}

/** Compact thematic illustration — lock + coin accents for MoneyTrack. */
function HeroArt({ variant }: { variant: "reset" | "success" | "error" }) {
  const sky = variant === "success" ? "#86efac" : variant === "error" ? "#fde68a" : "#7dd3fc";
  const accent = variant === "success" ? "#059669" : variant === "error" ? "#d97706" : "#0ea5e9";
  const lock = variant === "success" ? "#065f46" : variant === "error" ? "#92400e" : "#0c4a6e";

  return (
    <div className="relative mx-auto w-40 h-40 sm:w-48 sm:h-48 shrink-0">
      <svg viewBox="0 0 160 160" className="w-full h-full drop-shadow-[4px_4px_0_rgba(0,0,0,0.9)] dark:drop-shadow-none" aria-hidden>
        {/* soft ground blob */}
        <ellipse cx="80" cy="132" rx="48" ry="10" fill={sky} opacity="0.55" />
        {/* back card */}
        <rect x="28" y="36" width="88" height="88" rx="22" fill={sky} stroke="#111" strokeWidth="3" transform="rotate(-6 72 80)" />
        {/* front card */}
        <rect x="40" y="40" width="88" height="88" rx="22" fill="#fff" stroke="#111" strokeWidth="3" />
        {/* coin */}
        <circle cx="118" cy="48" r="16" fill="#fbbf24" stroke="#111" strokeWidth="3" />
        <circle cx="118" cy="48" r="9" fill="none" stroke="#111" strokeWidth="2" />
        <text x="118" y="52" textAnchor="middle" fontSize="12" fontWeight="800" fill="#111">
          $
        </text>
        {/* lock body */}
        <rect x="62" y="78" width="36" height="28" rx="6" fill={accent} stroke="#111" strokeWidth="3" />
        {/* lock shackle */}
        <path
          d="M70 78 V66 a10 10 0 0 1 20 0 V78"
          fill="none"
          stroke={lock}
          strokeWidth="5"
          strokeLinecap="round"
        />
        {/* keyhole */}
        <circle cx="80" cy="90" r="4" fill="#111" />
        <rect x="78" y="90" width="4" height="8" rx="1" fill="#111" />
        {/* sparkles */}
        <path d="M52 58 l2 5 5 2 -5 2 -2 5 -2 -5 -5 -2 5 -2 z" fill={accent} stroke="#111" strokeWidth="1.5" />
        <path d="M108 108 l1.5 3.5 3.5 1.5 -3.5 1.5 -1.5 3.5 -1.5 -3.5 -3.5 -1.5 3.5 -1.5 z" fill="#fbbf24" stroke="#111" strokeWidth="1.2" />
        {variant === "success" && (
          <path d="M72 92 l6 6 12 -14" fill="none" stroke="#065f46" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        )}
      </svg>
    </div>
  );
}

/**
 * Password-reset landing (legal-public).
 */
export default function AuthResetPassword() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [ready, setReady] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const strength = useMemo(() => evaluatePassword(password), [password]);
  const checks = useMemo(() => passwordChecks(password), [password]);
  const mismatch = confirm.length > 0 && password !== confirm;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: sessionError } = await supabase.auth.getSession();
        if (cancelled) return;
        if (sessionError || !data.session) {
          setError("重設連結無效或已過期。請回到 App 重新申請「忘記密碼」。");
          setReady(false);
          return;
        }
        setReady(true);
      } catch {
        if (!cancelled) {
          setError("無法驗證重設連結，請回到 App 重新申請忘記密碼。");
          setReady(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (password.length < 6) {
      setFormError("還差一點：請至少輸入 6 碼（可看上方密碼強度提示）。");
      return;
    }
    if (password !== confirm) {
      setFormError("兩次輸入的密碼不一樣，請再確認一次。");
      return;
    }
    setBusy(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setDone(true);
    } catch (err: any) {
      setFormError(friendlyResetError(err?.message || ""));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[100dvh] bg-background flex flex-col pt-safe pb-safe">
        <main className="flex-1 max-w-md mx-auto w-full px-5 py-6 sm:py-10 flex flex-col justify-center gap-5">
          <HeroArt variant="success" />
          <header className="space-y-1.5 text-center">
            <p className="text-[10px] font-bold tracking-wide text-muted-foreground uppercase">
              {PRODUCT_NAME}
            </p>
            <h1 className="text-2xl sm:text-3xl font-black text-foreground">密碼已重設成功</h1>
            <p className="text-sm font-medium text-muted-foreground">
              請回到 {PRODUCT_NAME_ZH} App，用新密碼登入。
            </p>
          </header>
          <div className="space-y-2.5">
            <button
              type="button"
              onClick={() => openNativeApp("auth/verified")}
              className="flex items-center justify-center w-full h-11 rounded-xl border-2 border-black dark:border-white/20 bg-black text-white dark:bg-white dark:text-black font-bold text-sm shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] dark:shadow-none"
            >
              打開 {PRODUCT_NAME} App
            </button>
            <Link
              href="/"
              className="flex items-center justify-center w-full h-11 rounded-xl border-2 border-black dark:border-white/20 bg-white dark:bg-zinc-900 font-bold text-sm shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] dark:shadow-none"
            >
              回到說明首頁
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col pt-safe pb-safe">
      <main className="flex-1 max-w-md mx-auto w-full px-5 py-5 sm:py-8 flex flex-col justify-center gap-4 sm:gap-5">
        <HeroArt variant={error ? "error" : "reset"} />
        <header className="space-y-1 text-center">
          <p className="text-[10px] font-bold tracking-wide text-muted-foreground uppercase">
            {PRODUCT_NAME}
          </p>
          <h1 className="text-2xl sm:text-3xl font-black text-foreground leading-tight">
            {error ? "連結無法使用" : "設定新密碼"}
          </h1>
          <p className="text-xs sm:text-sm font-medium text-muted-foreground leading-relaxed">
            {error || `完成後回到 ${PRODUCT_NAME_ZH} App 登入即可。`}
          </p>
        </header>

        {ready && !error ? (
          <form onSubmit={handleSubmit} className="space-y-3" noValidate>
            {/* Password fields + strength in one card */}
            <div className="rounded-2xl border-2 border-black dark:border-white/20 bg-white dark:bg-zinc-900 p-3.5 space-y-3 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] dark:shadow-none">
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-foreground">新密碼</label>
                <input
                  type="password"
                  placeholder="輸入新密碼"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setFormError(null);
                  }}
                  className="w-full h-11 px-3 rounded-xl border-2 border-black/80 dark:border-white/20 bg-background text-sm font-medium"
                  required
                  autoComplete="new-password"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-foreground">再輸入一次</label>
                <input
                  type="password"
                  placeholder="確認新密碼"
                  value={confirm}
                  onChange={(e) => {
                    setConfirm(e.target.value);
                    setFormError(null);
                  }}
                  className={`w-full h-11 px-3 rounded-xl border-2 bg-background text-sm font-medium ${
                    mismatch
                      ? "border-amber-500"
                      : "border-black/80 dark:border-white/20"
                  }`}
                  required
                  autoComplete="new-password"
                />
                {mismatch && (
                  <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/50 border border-amber-300 rounded-lg px-2.5 py-1.5">
                    兩次密碼還不一致，再對一下就好。
                  </p>
                )}
              </div>

              <div className="pt-1 border-t border-black/10 dark:border-white/10 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] font-bold text-foreground">密碼強度</p>
                  <p className={`text-[11px] font-bold ${strength.hintClass}`}>{strength.label}</p>
                </div>
                <div className="grid grid-cols-4 gap-1">
                  {[1, 2, 3, 4].map((step) => (
                    <div
                      key={step}
                      className={`h-2 rounded-full ${
                        strength.score >= step ? strength.barClass : "bg-zinc-100 dark:bg-zinc-800"
                      }`}
                    />
                  ))}
                </div>
                <ul className="grid grid-cols-2 gap-x-2 gap-y-1">
                  {checks.map((item) => (
                    <li key={item.text} className="flex items-center gap-1.5 text-[11px] font-medium">
                      <span
                        className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border border-black/20 ${
                          item.ok
                            ? "bg-emerald-300 text-black"
                            : "bg-zinc-100 dark:bg-zinc-800 text-muted-foreground"
                        }`}
                      >
                        {item.ok ? (
                          <Check className="h-2.5 w-2.5" strokeWidth={3} />
                        ) : (
                          <X className="h-2.5 w-2.5" />
                        )}
                      </span>
                      <span className={item.ok ? "text-foreground" : "text-muted-foreground"}>
                        {item.text}
                      </span>
                    </li>
                  ))}
                </ul>
                {password.length > 0 && password.length < 6 && (
                  <p className="text-[11px] font-semibold text-sky-800 dark:text-sky-200 bg-sky-50 dark:bg-sky-950/40 border border-sky-200 rounded-lg px-2.5 py-1.5">
                    目前少於 6 碼，還不能儲存；8 碼以上會更安心。
                  </p>
                )}
              </div>
            </div>

            {formError && (
              <p className="text-xs font-semibold text-amber-900 dark:text-amber-100 bg-amber-50 dark:bg-amber-950/40 border border-amber-300 rounded-xl px-3 py-2 text-center">
                {formError}
              </p>
            )}

            <button
              type="submit"
              disabled={busy || mismatch}
              className="flex items-center justify-center w-full h-11 rounded-xl border-2 border-black dark:border-white/20 bg-black text-white dark:bg-white dark:text-black font-bold text-sm disabled:opacity-50 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] dark:shadow-none"
            >
              {busy ? "更新中…" : "儲存新密碼"}
            </button>
          </form>
        ) : (
          <div className="space-y-2.5">
            <button
              type="button"
              onClick={() => openNativeApp("auth/verified")}
              className="flex items-center justify-center w-full h-11 rounded-xl border-2 border-black dark:border-white/20 bg-black text-white dark:bg-white dark:text-black font-bold text-sm shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] dark:shadow-none"
            >
              打開 {PRODUCT_NAME} App
            </button>
            <Link
              href="/"
              className="flex items-center justify-center w-full h-11 rounded-xl border-2 border-black dark:border-white/20 bg-white dark:bg-zinc-900 font-bold text-sm shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] dark:shadow-none"
            >
              回到說明首頁
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
